import {
  Box,
  IconButton,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AuthorList from './AuthorList';
import { useTranslation } from 'react-i18next';
import ListAltIcon from '@mui/icons-material/ListAlt';
import MobileDrawer from './MobileDrawer';
import TableOfContents from './TableOfContents';

const drawerWidth = 360;
const mobilePosition = { position: 'absolute', top: 0, bottom: 0, right: 0 };

interface DrawerProps {
  readonly tableOfContents: Element[];
  readonly title?: string;
  readonly authors: string[];
  closeDrawer: () => void;
  openDrawer: () => void;
  readonly open: boolean;
  readonly hasFootnotes: boolean;
}

export default function TableOfContentsDrawer({
  tableOfContents,
  title,
  authors,
  closeDrawer,
  openDrawer,
  open,
  hasFootnotes = false,
}: Readonly<DrawerProps>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobileVariant = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallVariant = useMediaQuery(theme.breakpoints.down('lg'));

  return (
    <>
      {!isMobileVariant ? (
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            width: open ? drawerWidth : 49,
            ...(isSmallVariant && mobilePosition),
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
            display: 'flex',
            flexShrink: 0,
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              width: '100%',
              position: 'sticky',
              top: 0,
              height: 'fit-content',
            }}
          >
            <Box sx={{ height: '100vh', overflowY: 'auto' }}>
              {!open && (
                <Stack
                  direction='row'
                  sx={{
                    justifyContent: 'center',
                  }}
                >
                  <IconButton
                    color='primary'
                    size='large'
                    sx={{
                      color: 'text.secondary',
                      marginTop: 6,
                    }}
                    onClick={openDrawer}
                  >
                    <ListAltIcon />
                  </IconButton>
                </Stack>
              )}
              {open && (
                <>
                  <Stack
                    direction='row'
                    sx={{
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: 6,
                      marginX: 2,
                    }}
                  >
                    <Typography variant='subtitle2'>
                      {t('article.tableOfContents')}
                    </Typography>
                    <IconButton onClick={closeDrawer}>
                      <CloseIcon />
                    </IconButton>
                  </Stack>
                  <Box
                    sx={{
                      overflow: 'auto',
                      marginTop: 3,
                    }}
                  >
                    {authors.length > 0 && (
                      <Box
                        sx={{
                          marginX: 2,
                          marginBottom: 1,
                        }}
                      >
                        <AuthorList authors={authors} variant='body2' />
                      </Box>
                    )}
                    <TableOfContents
                      tableOfContents={tableOfContents}
                      title={title}
                      hasFootnotes={hasFootnotes}
                      onLinkClick={closeDrawer}
                    />
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>
      ) : (
        <MobileDrawer
          open={open}
          closeDrawer={closeDrawer}
          openDrawer={openDrawer}
          title={t('article.tableOfContents')}
        >
          <TableOfContents
            tableOfContents={tableOfContents}
            title={title}
            hasFootnotes={hasFootnotes}
            onLinkClick={closeDrawer}
          />
        </MobileDrawer>
      )}
    </>
  );
}
